import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { Service, SortOrderDirection } from 'webapp-types';
import { OrderDirection } from 'webapp/enums/enums';
import type { SearchProps } from 'webapp/utils/filter';

import FilterSort, { type FilterSortSettings } from 'webapp/models-custom/filter-sort/base';
import type { FilterOption } from 'webapp/models-custom/filter-sort/filter';
import type Team from 'webapp/models-custom/team';

export default class FilterSortTeam extends FilterSort<Team> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/teams') declare mmTeamsService: Service.ModelManager.Teams;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;

	@tracked override sortOrder: SortOrderDirection = OrderDirection.DESC;

	override settingsNamePrefix = 'teamsFilterSettings';

	override searchProps: SearchProps = {
		name: 1,
	};

	constructor(options: FilterSortSettings<Team>) {
		super(options);

		const selectedFilters = this.readSavedSelectedFilters();

		this.createFilter<number>({
			id: 1,
			label: 'Owned by',
			name: 'owner',
			selected: selectedFilters[1] as number | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.ownerOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((selectedId) => {
					if (selectedId === -1) {
						// exception: all others
						return !item.isOwnedByMe;
					} else {
						return item.ownerId === selectedId;
					}
				});
			},
		});

		this.createFilter<number>({
			id: 2,
			label: 'Members',
			name: 'members',
			selected: selectedFilters[2] as number | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.showMembers;
			},

			filterFn: function (item, selected) {
				return selected.some((selectedId) => {
					if (selectedId === -1) {
						// exception: all others
						return !item.isOwnedByMe;
					} else {
						return item.teamMembers.some((member) => member.id === selectedId);
					}
				});
			},
		});

		this.createFilter<number>({
			id: 3,
			label: 'Show',
			name: 'show',
			selected: selectedFilters[3] as number | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.showOptions;
			},
			filterFn: (item, selected) => {
				return selected.some((selectedId) => {
					if (selectedId === -1) {
						return !item.isUserMember;
					} else {
						return item.isUserMember;
					}
				});
			},
		});
	}

	get ownerOptions(): FilterOption<number>[] {
		const allItemCountByOwner: Record<number, number> = {};

		this.mmTeamsService.savedRecords.forEach((item) => {
			const ownerId = item.ownerId;
			if (!ownerId) {
				return;
			}

			if (!allItemCountByOwner[ownerId]) {
				allItemCountByOwner[ownerId] = 0;
			}

			allItemCountByOwner[ownerId]++;
		});

		return this.mmUsersService.getFilterOptionsForUsers(allItemCountByOwner);
	}

	get showOptions(): FilterOption<number>[] {
		const activeUserId = this.currentUserService.numericId!;

		const list: FilterOption<number>[] = [
			{ id: 0, text: 'All', notCombinable: true },
			{ id: activeUserId, text: 'Joined spaces', notCombinable: true },
			{ id: -1, text: 'Other spaces', notCombinable: true },
		];

		return list;
	}

	get showMembers(): FilterOption<number>[] {
		const allItemCountByOwner: Record<string, number> = {};

		this.allItems.forEach((team) => {
			team.activeTeamMembers.forEach((member) => {
				const userId = member.id;
				if (!allItemCountByOwner[userId]) {
					allItemCountByOwner[userId] = 0;
				}
				allItemCountByOwner[userId]++;
			});
		});
		return this.mmUsersService.getFilterOptionsForUsers(allItemCountByOwner);
	}
}
